import { RemoteAudioTrack } from 'livekit-client';
import { useEffect, useMemo, useRef } from 'react';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';

type Props = {
  meetId: string;
  index: number;
  audioTrack: RemoteAudioTrack;
};

export const VoiceChannelAudioStream = ({
  meetId,
  audioTrack,
  index,
}: Props) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioLoaded = useRef(false);

  const { myMeetId, localParticipant, remoteParticipants } =
    useLivekitRTCState();

  const muted = useMemo(
    () =>
      localParticipant.deaf ||
      remoteParticipants[meetId].localMuted ||
      remoteParticipants[meetId].muted,
    [localParticipant.deaf, meetId, remoteParticipants],
  );

  useEffect(() => {
    if (audioLoaded.current || !audioTrack) return;
    audioTrack.attach(audioRef.current as HTMLAudioElement);
    audioLoaded.current = true;
    return () => {
      audioLoaded.current = false;
      audioTrack.detach(audioRef.current as HTMLAudioElement);
      audioTrack.stop();
    };
  }, [audioTrack]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = remoteParticipants[meetId].volume;
  }, [meetId, remoteParticipants[meetId].volume]);

  if (!audioTrack || meetId === myMeetId) return null;

  return (
    // biome-ignore lint/a11y/useMediaCaption: <explanation>
    <audio
      ref={audioRef}
      autoPlay
      id={`meet_audio_${audioTrack.sid}_${index}`}
    />
  );
};
